import React from 'react';
import { capitalize } from '@fiverr-private/futile';
import pathfinder from '@fiverr-private/pathfinder';
import { I18n } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { Section, Grid, GridItem, Stack, Container } from '@fiverr-private/layout_components';
import { Typography, Link } from '@fiverr-private/typography';
import { RelatedExpertise, RelatedSkill } from '../../../shared/types';
import { sendBqEvent } from '../../../events/bigQuery';
import { translateSkillName } from '../../../utils';

interface Props {
    isPro: boolean;
    category: string;
    expertises: RelatedExpertise[];
    relatedSkills: RelatedSkill[];
    slug?: string;
}

const TRANSLATIONS_KEY = 'skill_page_perseus.similar_services';

const getSkillName = ({ skillId, skillSlug, locale }: { skillId: string; skillSlug: string; locale: string }) => {
    const translated = translateSkillName({ skillId, skillSlug, locale });
    return translated ? capitalize(translated) : capitalize(skillSlug).replace(/-/g, ' ');
};

const expertiseNameToSlug = (skillName: string) => skillName.replace(/\s+/g, '-').replace(/\./g, '').toLowerCase();

export const SimilarServices: React.FC<Props> = ({ category, isPro, expertises, relatedSkills, slug }) => {
    const { locale } = getContext();
    const getLink = (skillSlug: string): string =>
        pathfinder('skill', { skill: skillSlug }, { query: { source: 'skill_pages' } });

    const onSkillClick = (text: string) => {
        sendBqEvent('user_action', {
            type: 'skill_page.skill_click',
            actionType: 'click',
            element: {
                type: 'button',
                text,
                name: slug,
            },
        });
    };

    const relatedSkillNames = relatedSkills?.length
        ? relatedSkills.map((skill) => ({ skillId: skill.skill_id, skillSlug: skill.slug }))
        : expertises.map((e) => ({ skillId: e.id, skillSlug: expertiseNameToSlug(e.name) }));

    return (
        <Container marginTop={{ default: '10', sm: '10', md: '16', lg: '16', xl: '16' }}>
            <Section
                backgroundColor={isPro ? 'business_100' : 'grey_100'}
                fullWidth
                paddingTop={{ default: '7', sm: '8', md: '14' }}
                paddingBottom={{ default: '9', sm: '8', md: '16' }}
            >
                <Stack
                    direction="column"
                    centered
                    gap="0"
                    paddingTop="0"
                    paddingBottom="0"
                    padding="0"
                    paddingLeft="8"
                    paddingRight="6"
                >
                    <Typography as="h3" marginBottom={{ default: '1', sm: '2' }}>
                        <I18n k={`${TRANSLATIONS_KEY}.title`} params={{ category }} />
                    </Typography>
                    <Grid
                        paddingTop="10"
                        gap="6"
                        templateColumns={{
                            default: '100%',
                            sm: '100%',
                            md: '33%_33%_33%',
                            lg: '25%_25%_25%_25%',
                            xl: '25%_25%_25%_25%',
                        }}
                    >
                        {relatedSkillNames.map(({ skillId, skillSlug }) => (
                            <GridItem key={slug} onClick={() => onSkillClick(skillSlug)}>
                                <Link href={getLink(skillSlug)}>{getSkillName({ skillId, skillSlug, locale })}</Link>
                            </GridItem>
                        ))}
                    </Grid>
                </Stack>
            </Section>
        </Container>
    );
};
